import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "./PageTitleCompoent";
import DietPlanCard from "./DietPlanCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import api from "../api"; // ✅ centralized API instance
import "../css/DietPlanScreen.css";

export default function DietPlanScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const [packages, setPackages] = useState([]);
  const [editModalData, setEditModalData] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePackageData, setDeletePackageData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Fetch diet plan packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await api.get("/dietplan-packages/");

        const formatted = res.data.map((pkg) => ({
          id: pkg.id,
          title: pkg.package_name,
          points: pkg.plan || [],
          bottomText: pkg.focus || "",
          createdAt: pkg.created_at,
          updatedAt: pkg.updated_at,
        }));

        setPackages(formatted);
      } catch (err) {
        console.error("Failed to fetch diet packages:", err);
        setError("Failed to load packages.");
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // ✅ Handle new package after add
  useEffect(() => {
    if (location.state?.newPackage) {
      setPackages((prev) => [...prev, location.state.newPackage]);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleBack = () => navigate(-1);

  // ✅ Delete package
  const openDeleteModal = (pkg) => {
    setDeletePackageData(pkg);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletePackageData(null);
  };

  const confirmDelete = async () => {
    if (deletePackageData?.id) {
      try {
        // 🔑 delete package by ID
        await api.delete(`/dietplan-packages/${deletePackageData.id}/`);

        // 🔑 update local state after delete
        setPackages((prev) =>
          prev.filter((pkg) => pkg.id !== deletePackageData.id)
        );
      } catch (err) {
        console.error("Error deleting package:", err);
        alert("Failed to delete package. Please try again.");
      }
    }
    closeDeleteModal();
  };

  // ✅ Edit package
  const handleEditClick = (pkg) => {
    setEditModalData(pkg);
    setEditingPackage({ ...pkg });
  };

  const handleSave = async () => {
    try {
      await api.put(`/dietplan-packages/${editingPackage.id}/`, {
        package_name: editingPackage.title,
        plan: editingPackage.points,
        focus: editingPackage.bottomText,
      });

      setPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === editingPackage.id ? { ...editingPackage } : pkg
        )
      );

      setEditModalData(null);
      setEditingPackage(null);
    } catch (err) {
      console.error("Error updating package:", err);
      alert("Failed to update package.");
    }
  };

  const handleAddPackageClick = () =>
    navigate("/add_package", { state: { type: "DietPlan" } });

  return (
    <div className="service-diet-plan-container">
      <Sidebar />
      <div className="service-diet-plan-main">
        <Header />
        <div className="service-diet-plan-content">
          <div className="service-diet-plan-heading-row">
            <PageTitle title="Diet Plan" onBack={handleBack} />
            {/* <div className="add-package-sec">
              <button
                className="service-diet-plan-button"
                onClick={handleAddPackageClick}
              >
                <IoMdAdd size={20} color="#eba8bf" />
                <span className="service-diet-plan-text">Add Package</span>
              </button>
            </div> */}
          </div>

          {loading && <p>Loading diet packages...</p>}
          {error && <p className="error">{error}</p>}

          <div className="service-diet-plan-grid">
            {packages.map((pkg) => (
              <DietPlanCard
                key={pkg.id}
                {...pkg}
                onAddService={() => handleEditClick(pkg)}
                onDelete={() => openDeleteModal(pkg)}
                isEditable={false}
              />
            ))}
          </div>

          {/* ✅ Edit Modal */}
          {editModalData && editingPackage && (
            <div className="diet-plan-modal-overlay">
              <div className="diet-plan-modal-box">
                <DietPlanCard
                  {...editingPackage}
                  isEditable={true}
                  onClose={() => {
                    setEditModalData(null);
                    setEditingPackage(null);
                  }}
                  onSave={handleSave}
                />
              </div>
            </div>
          )}

          {/* ✅ Delete Confirmation Modal */}
          <DeleteWithConfirmation
            isOpen={isDeleteModalOpen}
            onCancel={closeDeleteModal}
            onConfirm={confirmDelete}
            title={`Delete ${deletePackageData?.title || "Package"}?`}
            message="Are you sure you want to delete this package? This action cannot be undone."
          />
        </div>
      </div>
    </div>
  );
}
